// Diário › Projeto de Vida (0.65): metas, objetivos e sonhos com prazo (curto, médio, longo), data opcional
// com contagem regressiva, passos, anotações, hábitos ligados e barra de progresso. "Concluído" leva ao Mural.
import { useEffect, useState } from 'react';
import { Ditado } from '../Ditado';
import { Alert, Pressable, Text, TextInput, View } from 'react-native';
import { useAllDocs } from '../../store/store';
import { gravar, gravarHabitos, gravarMetas, lerHabitos, lerInsp, lerMetas, lerMudar, novaMeta, novoHabito, useDiario, type Habito, type MetaVida, type Prazo, type TipoMeta, abrirDaLixeira, selarParaLixeira } from '../../store/diario';
import type { Item } from '../../store/types';
import { AcoesItem } from '../Acoes';
import { Ordenavel } from '../Arrastar';
import { Arquivados, useSelecao } from '../Selecao';
import { EmojiPicker } from '../EmojiPicker';
import { PaginaEditor } from '../PaginaEditor';
import { PickerModal } from '../Pickers';
import { ActionSheet, Btn, Chip, Field, Screen, Sheet, type Action } from '../ui';
import { C, alpha } from '../theme';
import { Barra, Corpo, Recolhe, concluidoFora, dataLonga, tarefaDe, useEnviarDiario } from './comum';
import { HabitoSheet } from './Habitos';
import { progressoMeta } from '../../lib/habitos';
import { useOrganizar } from '../../lib/organizar';
import { paraLixeira, registrarRestauro } from '../../lib/lixeira';
import { diasAte } from '../../modulos/util';
import { parseYmd, today, uid, ymd } from '../../lib/date';
import { N_, t } from '../../lib/i18n';
import { useBotaoNovo } from '../../lib/botaoNovo';
import { useCaixas } from '../../lib/caixas';

export const TIPOS: [TipoMeta, string, string][] = [['meta', N_('Meta'), '🎯'], ['objetivo', N_('Objetivo'), '🏁'], ['sonho', N_('Sonho'), '⭐']];
export const PRAZOS: [Prazo, string][] = [['curto', N_('Curto prazo')], ['medio', N_('Médio prazo')], ['longo', N_('Longo prazo')]];
const nomeTipo = (x: TipoMeta) => t(TIPOS.find(y => y[0] === x)?.[1] ?? '');
const iconeTipo = (x: TipoMeta) => TIPOS.find(y => y[0] === x)?.[2] ?? '🎯';
/** Ícone do item: o escolhido pela pessoa; se não escolheu (vazio, ou o ícone padrão de OUTRO tipo, como o 🎯
 *  que antes era gravado em todo item novo), o ícone do tipo: Meta 🎯, Objetivo 🏁, Sonho ⭐ (🌟 era o padrão antigo). */
export const iconeMeta = (m: Pick<MetaVida, 'icone' | 'tipo'>) =>
  !m.icone || (m.icone === '🌟' && m.tipo === 'sonho') || TIPOS.some(([k, , e]) => k !== m.tipo && e === m.icone) ? iconeTipo(m.tipo) : m.icone;

registrarRestauro('diario-meta', (c: unknown) => { const m = abrirDaLixeira<MetaVida>(c); if (!lerMetas().some(x => x.id === m.id)) gravarMetas([...lerMetas(), m]); });

/** Contagem regressiva: "faltam 82 dias". */
export function faltam(data?: string | null) {
  if (!data) return '';
  const n = diasAte(data);
  return n > 1 ? t('faltam {n} dias', { n }) : n === 1 ? t('falta 1 dia') : n === 0 ? t('é hoje!') : t('passou há {n} dias', { n: -n });
}

export function ProjetoVida({ cor, abrirId, onBack, onInsp }: { cor: string; abrirId?: string | null; onBack: () => void; onInsp: (metaId: string) => void }) {
  useDiario();
  const docs = useAllDocs();
  const metas = lerMetas();
  const habitos = lerHabitos();
  const [filtro, setFiltro] = useState<Prazo | 'todos'>('todos');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const [ed, setEd] = useState<MetaVida | null>(null);
  // 11d F: o + cria uma meta, objetivo ou sonho (o mesmo botão do fim da lista)
  useBotaoNovo({ rotulo: t('Nova meta, objetivo ou sonho'), fn: () => setEd(novaMeta({ prazo: filtro === 'todos' ? 'curto' : filtro, icone: iconeTipo('meta') })) });
  const org = useOrganizar('diario-vida');
  const enviarD = useEnviarDiario();
  const [mudar, setMudar] = useState(lerMudar());

  // concluída em outra aba (tarefa marcada no My Planner): conta aqui também
  useEffect(() => {
    let mudou = false;
    const l = metas.map(m => { if (m.concluida) return m; const d = concluidoFora('meta:' + m.id); if (d) { mudou = true; return { ...m, concluida: d }; } return m; });
    if (mudou) gravarMetas(l);
  }, [docs.planner, docs.anotacoes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (abrirId) { const m = metas.find(x => x.id === abrirId); if (m) setEd(m); } }, [abrirId]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = (m: MetaVida) => gravarMetas(metas.some(x => x.id === m.id) ? metas.map(x => (x.id === m.id ? m : x)) : [...metas, m]);
  const excluir = (m: MetaVida) => { paraLixeira('diario', 'diario-meta', t('Meta do Diário'), selarParaLixeira(m)); gravarMetas(lerMetas().filter(x => x.id !== m.id)); };
  const duplicar = (m: MetaVida) => gravarMetas([...metas, { ...JSON.parse(JSON.stringify(m)), id: uid(), titulo: m.titulo + ' ' + t('(cópia)'), concluida: null }]);
  const abertas = metas.filter(m => !m.concluida);
  const { ativos, arquivados } = org.separar(abertas.filter(m => filtro === 'todos' || m.prazo === filtro), m => m.id);
  const concluidas = metas.filter(m => m.concluida && (filtro === 'todos' || m.prazo === filtro));
  const sel = useSelecao({ itens: ativos, chave: m => m.id, cor, onArquivar: ids => org.arquivar(ids), onExcluir: ids => ids.forEach(id => { const m = metas.find(x => x.id === id); if (m) excluir(m); }) });
  const prog = (m: MetaVida) => progressoMeta(m, habitos, docs, id => tarefaDe('hab:' + id));
  const ordenar = (l: MetaVida[]) => { const pos = new Map(l.map((m, i) => [m.id, i])); gravarMetas(metas.map(m => (pos.has(m.id) ? { ...m, ordem: pos.get(m.id) } : m))); };
  const emOrdem = (l: MetaVida[]) => [...l].sort((a, b) => (a.ordem ?? 1e9) - (b.ordem ?? 1e9));

  const cartao = (m: MetaVida) => {
    const p = prog(m);
    return (
      <Pressable onPress={() => setEd(m)} style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(12), gap: cx.gap(6), borderWidth: 1, borderColor: C.line }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingRight: 22 }}>
          <Text style={{ fontSize: cx.icone(26) }}>{iconeMeta(m)}</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '700', fontSize: 15 }} numberOfLines={2}>{m.titulo || t('Sem título')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{[nomeTipo(m.tipo), t(PRAZOS.find(x => x[0] === m.prazo)?.[1] ?? ''), m.data ? dataLonga(m.data) : ''].filter(Boolean).join(' · ')}</Text>
          </View>
        </View>
        {!!m.data && <Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>⏳ {faltam(m.data)}</Text>}
        <Barra p={p} cor={cor} />
        <Text style={{ color: C.sub, fontSize: 11 }}>{Math.round(p * 100)}%{m.passos.length ? ' · ' + t('{f}/{n} passos', { f: m.passos.filter(x => x.done).length, n: m.passos.length }) : ''}</Text>
      </Pressable>
    );
  };
  const lista = (l: MetaVida[]) => (
    <Ordenavel itens={emOrdem(l)} chave={m => m.id} onOrdem={ordenar} selecao={sel.prop} render={m => (
      <AcoesItem lembrar={{ aba: 'diario', id: m.id, titulo: t('📔 Diário'), item: 'meta:' + m.id }} nome={m.titulo} onExcluir={() => excluir(m)} onDuplicar={() => duplicar(m)} onArquivar={() => org.arquivar(m.id)}
        menuExtra={[{ label: '📤 ' + t('Enviar para outra aba'), onPress: () => enviarD('meta:' + m.id, m.titulo, iconeMeta(m), m.data) }]}
        fixar={{ on: org.fixado(m.id), alternar: () => org.alternarFixo(m.id) }}>
        {cartao(m)}
      </AcoesItem>
    )} />
  );

  return (
    <Screen title={t('Projeto de Vida')} onBack={onBack}>
      <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 16, padding: 12, gap: 6, borderWidth: 1, borderColor: alpha(cor, 0.35) }}>
        <Text style={{ color: cor, fontWeight: '800' }}>🔄 {t('O que quero mudar agora')}</Text>
        <View>
          <TextInput multiline value={mudar} onChangeText={v => { setMudar(v); gravar('mudar', v); }} placeholder={t('Escreva o que você quer mudar a partir de hoje')} placeholderTextColor={C.mute}
            style={{ color: C.text, fontSize: 15, minHeight: 50, textAlignVertical: 'top', paddingRight: 36 }} />
          <View style={{ position: 'absolute', right: 0, bottom: 0 }}><Ditado pequeno cor={cor} onTexto={tx => { const v = mudar ? mudar + ' ' + tx : tx; setMudar(v); gravar('mudar', v); }} /></View>
        </View>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <Chip small cor={cor} on={filtro === 'todos'} label={t('Todos')} onPress={() => setFiltro('todos')} />
        {PRAZOS.map(([k, n]) => <Chip key={k} small cor={cor} on={filtro === k} label={t(n)} onPress={() => setFiltro(k)} />)}
      </View>
      {sel.barra}
      {!abertas.length && <Text style={{ color: C.sub }}>{t('Crie sua primeira meta, objetivo ou sonho com o botão + abaixo.')}</Text>}
      {filtro === 'todos' ? PRAZOS.map(([k, n]) => {
        const l = ativos.filter(m => m.prazo === k);
        return l.length ? <View key={k} style={{ gap: 8 }}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t(n).toUpperCase()} ({l.length})</Text>{lista(l)}</View> : null;
      }) : lista(ativos)}
      <Arquivados itens={arquivados} chave={m => m.id} cor={cor} render={m => <Text style={{ color: C.text }}>{iconeMeta(m)} {m.titulo}</Text>}
        onRestaurar={m => org.restaurar(m.id)} onExcluir={m => Alert.alert(t('Excluir {nome}?', { nome: m.titulo }), t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluir(m) }])} />
      {!!concluidas.length && (
        <Recolhe titulo={`🏆 ${t('Concluídos')} (${concluidas.length})`}>
          {concluidas.map(m => (
            <Pressable key={m.id} onPress={() => setEd(m)} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), padding: cx.pad(10), flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              <Text style={{ fontSize: cx.icone(20) }}>{iconeMeta(m)}</Text>
              <Text style={{ color: C.text, flex: 1, textDecorationLine: 'line-through' }}>{m.titulo}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>✓ {dataLonga(m.concluida!)}</Text>
            </Pressable>
          ))}
        </Recolhe>
      )}
      <Pressable onPress={() => setEd(novaMeta({ prazo: filtro === 'todos' ? 'curto' : filtro, icone: iconeTipo('meta') }))} style={{ backgroundColor: cor, borderRadius: 14, padding: 14, alignItems: 'center', marginTop: 6 }}>
        <Text style={{ color: '#fff', fontWeight: '800' }}>+ {t('Nova meta, objetivo ou sonho')}</Text>
      </Pressable>
      <View style={{ height: 60 }} />
      <MetaSheet meta={ed} cor={cor} onFechar={() => setEd(null)} onSalvar={m => { salvar(m); }} onExcluir={m => { excluir(m); setEd(null); }}
        onEnviar={m => enviarD('meta:' + m.id, m.titulo, iconeMeta(m), m.data)} onInsp={onInsp} />
    </Screen>
  );
}

function MetaSheet({ meta, cor, onFechar, onSalvar, onExcluir, onEnviar, onInsp }: { meta: MetaVida | null; cor: string; onFechar: () => void; onSalvar: (m: MetaVida) => void;
  onExcluir: (m: MetaVida) => void; onEnviar: (m: MetaVida) => void; onInsp: (metaId: string) => void }) {
  const docs = useAllDocs();
  const [m, setM] = useState<MetaVida | null>(meta);
  const [emoji, setEmoji] = useState(false);
  const [data, setData] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [hab, setHab] = useState<Habito | null>(null);
  const [ligar, setLigar] = useState<Action[] | null>(null);
  const enviarD = useEnviarDiario();
  if ((meta?.id ?? null) !== aberto) { setAberto(meta?.id ?? null); setM(meta); }
  if (!m) return <Sheet visible={false} onClose={onFechar} title=""><View /></Sheet>;
  const set = (p: Partial<MetaVida>) => setM(x => (x ? { ...x, ...p } : x));
  const habitos = lerHabitos();
  const ligados = habitos.filter(h => m.habitos.includes(h.id) || h.meta === m.id);
  const insp = lerInsp().filter(i => i.meta === m.id);
  const fechar = () => { if (m.titulo.trim() || m.passos.length) onSalvar({ ...m, titulo: m.titulo.trim(), passos: m.passos.filter(p => p.text.trim()) }); onFechar(); };
  const p = progressoMeta(m, habitos, docs, id => tarefaDe('hab:' + id));
  const mudarPasso = (id: string, f: (i: Item) => Item | null) => set({ passos: m.passos.map(x => (x.id === id ? f(x) : x)).filter(Boolean) as Item[] });
  return (
    <Sheet visible={!!meta} onClose={fechar} title={nomeTipo(m.tipo)} right={<Pressable onPress={fechar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Pronto')}</Text></Pressable>}>
      <View style={{ flex: 1 }}>
        <Corpo>
          <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
            <Pressable onPress={() => setEmoji(true)} style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}><Text style={{ fontSize: 28 }}>{iconeMeta(m)}</Text></Pressable>
            <Field value={m.titulo} onChangeText={v => set({ titulo: v })} placeholder={t('Título (ex.: Ter uma boa alimentação)')} style={{ flex: 1 }} />
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {TIPOS.map(([k, n, e]) => <Chip key={k} small cor={cor} on={m.tipo === k} label={`${e} ${t(n)}`} onPress={() => set({ tipo: k, icone: iconeMeta(m) === iconeTipo(m.tipo) ? iconeTipo(k) : m.icone })} />)}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {PRAZOS.map(([k, n]) => <Chip key={k} small cor={cor} on={m.prazo === k} label={t(n)} onPress={() => set({ prazo: k })} />)}
          </View>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Chip cor={cor} on={!!m.data} label={'📅 ' + (m.data ? dataLonga(m.data) : t('Data (opcional)'))} onPress={() => setData(true)} />
            {!!m.data && <Text style={{ color: cor, fontWeight: '700' }}>⏳ {faltam(m.data)}</Text>}
            {!!m.data && <Pressable onPress={() => set({ data: null })} hitSlop={8}><Text style={{ color: C.mute }}>✕</Text></Pressable>}
          </View>
          <View style={{ gap: 4 }}>
            <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('PROGRESSO')} · {Math.round(p * 100)}%</Text>
            <Barra p={p} cor={cor} />
          </View>

          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('PASSOS')}</Text>
          {m.passos.map(ps => (
            <View key={ps.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Pressable onPress={() => mudarPasso(ps.id, x => ({ ...x, done: !x.done }))} hitSlop={8}
                style={{ width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: ps.done ? cor : C.mute, backgroundColor: ps.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                {ps.done && <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>✓</Text>}
              </Pressable>
              <TextInput value={ps.text} onChangeText={v => mudarPasso(ps.id, x => ({ ...x, text: v }))} placeholder={t('Passo')} placeholderTextColor={C.mute} blurOnSubmit={false}
                onSubmitEditing={() => { const n = { id: uid(), text: '', done: false }; const i = m.passos.findIndex(x => x.id === ps.id); const a = [...m.passos]; a.splice(i + 1, 0, n); set({ passos: a }); }}
                onKeyPress={e => { if (e.nativeEvent.key === 'Backspace' && !ps.text) mudarPasso(ps.id, () => null); }}
                style={{ flex: 1, color: C.text, paddingVertical: 6, textDecorationLine: ps.done ? 'line-through' : 'none' }} />
            </View>
          ))}
          <Pressable onPress={() => set({ passos: [...m.passos, { id: uid(), text: '', done: false }] })}><Text style={{ color: cor, fontWeight: '700' }}>+ {t('Passo')}</Text></Pressable>

          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('HÁBITOS LIGADOS')}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {ligados.map(h => (
              <Chip key={h.id} small cor={cor} on label={`${h.icone} ${h.nome}`} onPress={() => {
                set({ habitos: m.habitos.filter(x => x !== h.id) });
                if (h.meta === m.id) gravarHabitos(lerHabitos().map(x => (x.id === h.id ? { ...x, meta: null } : x)));
              }} />
            ))}
            <Chip small cor={cor} on={false} label={'+ ' + t('Novo hábito')} onPress={() => {
              if (m.titulo.trim()) onSalvar({ ...m, titulo: m.titulo.trim(), passos: m.passos.filter(p => p.text.trim()) });
              setHab(novoHabito({ meta: m.id }));
            }} />
            {habitos.some(h => !h.formado && !ligados.includes(h)) && (
              <Chip small cor={cor} on={false} label={'🔗 ' + t('Ligar um hábito que já existe')} onPress={() => setLigar(habitos.filter(h => !h.formado && !ligados.includes(h)).map(h => ({
                label: `${h.icone} ${h.nome || t('Sem nome')}`,
                onPress: () => {
                  setM(x => (x ? { ...x, habitos: x.habitos.includes(h.id) ? x.habitos : [...x.habitos, h.id] } : x));
                  if (!h.meta) gravarHabitos(lerHabitos().map(x => (x.id === h.id ? { ...x, meta: m.id } : x)));
                },
              })))} />
            )}
          </View>

          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('ANOTAÇÕES')}</Text>
          <PaginaEditor blocos={m.blocos} onChange={bs => set({ blocos: bs })} cor={cor} aba="diario" />

          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 6 }}>{t('INSPIRAÇÃO')}</Text>
          {insp.map(i => <Text key={i.id} style={{ color: C.text, fontStyle: 'italic' }} numberOfLines={2}>✨ {i.texto || i.url}</Text>)}
          <Pressable onPress={() => { fechar(); onInsp(m.id); }}><Text style={{ color: cor, fontWeight: '700' }}>+ {t('Ligar um texto de motivação (Inspiração)')}</Text></Pressable>

          <View style={{ gap: 8, marginTop: 12 }}>
            {m.concluida
              ? <Btn cor={cor} outline label={'↩︎ ' + t('Desfazer "Concluído" ({d})', { d: dataLonga(m.concluida) })} onPress={() => set({ concluida: null })} />
              : <Btn cor={cor} label={'🏆 ' + t('Concluído')} onPress={() => { onSalvar({ ...m, concluida: today() }); onFechar(); Alert.alert('🎉 ' + t('Parabéns!'), t('"{nome}" foi para o Mural, em Conquistas.', { nome: m.titulo })); }} />}
            <Btn cor={cor} outline label={'📤 ' + t('Enviar o título para outra aba')} onPress={() => { onSalvar(m); onEnviar(m); }} />
            <Pressable onPress={() => Alert.alert(t('Excluir {nome}?', { nome: m.titulo || t('este item') }), t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => onExcluir(m) }])} style={{ padding: 10, alignItems: 'center' }}>
              <Text style={{ color: C.danger }}>🗑 {t('Excluir')}</Text>
            </Pressable>
          </View>
        </Corpo>
      </View>
      <EmojiPicker visible={emoji} cor={cor} onClose={() => setEmoji(false)} onPick={e => set({ icone: e })} />
      <HabitoSheet hab={hab} cor={cor} onFechar={() => setHab(null)}
        onSalvar={h => {
          const l = lerHabitos();
          gravarHabitos(l.some(x => x.id === h.id) ? l.map(x => (x.id === h.id ? h : x)) : [...l, h]);
          setM(x => (x ? { ...x, habitos: h.meta === x.id ? (x.habitos.includes(h.id) ? x.habitos : [...x.habitos, h.id]) : x.habitos.filter(y => y !== h.id) } : x));
        }}
        onExcluir={h => {
          if (lerHabitos().some(x => x.id === h.id)) { paraLixeira('diario', 'diario-habito', t('Hábito do Diário'), selarParaLixeira(h)); gravarHabitos(lerHabitos().filter(x => x.id !== h.id)); }
          setM(x => (x ? { ...x, habitos: x.habitos.filter(y => y !== h.id) } : x)); setHab(null);
        }}
        onEnviar={h => enviarD('hab:' + h.id, h.nome, h.icone)} />
      <ActionSheet title={t('Ligar um hábito que já existe')} actions={ligar} onClose={() => setLigar(null)} />
      {data && <PickerModal mode="date" cor={cor} value={m.data ? parseYmd(m.data) : new Date()} onClose={() => setData(false)} onPick={d => set({ data: ymd(d) })} />}
    </Sheet>
  );
}
